import { useEffect, useState } from "react";
import { Badge } from "@zweb/ui";
import { apiFetch } from "../lib/api";

interface OnboardingStatus {
  currentStep: string;
  company: {
    id: string;
    name: string;
    email: string;
  } | null;
  documents: Array<{
    id: string;
    name: string;
    type: string;
    url: string;
  }>;
  deployment: {
    id: string;
    status: string;
    currentStage: string;
  } | null;
  schedule: {
    id: string;
    startAt: string;
    status: string;
  } | null;
}

const steps = [
  { key: "registration", label: "Cadastro", description: "Informações da empresa" },
  { key: "documents", label: "Documentos", description: "Upload de documentos" },
  { key: "scheduling", label: "Agendamento", description: "Agendar implantação" },
  { key: "deployment", label: "Implantação", description: "Processo de implantação" },
  { key: "evaluation", label: "Avaliação", description: "Avaliar implantação" },
  { key: "completed", label: "Concluído", description: "Onboarding completo" },
];

const stepLabels: Record<string, string> = {
  registration: "Cadastro",
  documents: "Documentos",
  scheduling: "Agendamento",
  deployment: "Implantação",
  evaluation: "Avaliação",
  completed: "Concluído",
};

export default function OnboardingPage() {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  // Form states
  const [companyName, setCompanyName] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [segment, setSegment] = useState("");
  const [size, setSize] = useState("medium");

  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("document");
  const [docUrl, setDocUrl] = useState("");

  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [notes, setNotes] = useState("");

  const [score, setScore] = useState(5);
  const [evalNotes, setEvalNotes] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch("/api/onboarding/status")
      .then((json) => {
        if (json.success) {
          setStatus(json.data);
          const stepIndex = steps.findIndex((s) => s.key === json.data.currentStep);
          setActiveStep(stepIndex >= 0 ? stepIndex : 0);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleRegisterCompany = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const json = await apiFetch("/api/onboarding/start", {
        method: "POST",
        body: JSON.stringify({
          companyName,
          tradeName,
          cnpj,
          email,
          phone,
          whatsapp,
          segment,
          size,
        }),
      });
      if (json.success) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Erro ao registrar empresa:", error);
    }
  };

  const handleUploadDocument = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const json = await apiFetch("/api/onboarding/documents", {
        method: "POST",
        body: JSON.stringify({
          name: docName,
          type: docType,
          url: docUrl,
          size: 1024,
          mimeType: "application/pdf",
        }),
      });
      if (json.success) {
        setDocName("");
        setDocUrl("");
        window.location.reload();
      }
    } catch (error) {
      console.error("Erro ao enviar documento:", error);
    }
  };

  const handleSchedule = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const json = await apiFetch("/api/onboarding/schedule", {
        method: "POST",
        body: JSON.stringify({
          preferredDate,
          preferredTime,
          notes,
        }),
      });
      if (json.success) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Erro ao agendar:", error);
    }
  };

  const handleEvaluate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const json = await apiFetch("/api/onboarding/evaluate", {
        method: "POST",
        body: JSON.stringify({
          score,
          notes: evalNotes,
        }),
      });
      if (json.success) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Erro ao avaliar:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/dashboard" className="text-sm font-medium hover:text-accent-600">Dashboard</a>
            <a href="/onboarding" className="text-sm font-medium text-accent-600">Onboarding</a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="container py-8">
        <div className="mx-auto max-w-4xl">
          <h1 className="text-3xl font-bold">Onboarding</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Complete as etapas abaixo para começar a usar o ZWeb
          </p>

          {/* Progress Steps */}
          <div className="mt-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.key} className="flex flex-1 flex-col items-center">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                      index < activeStep
                        ? "border-green-500 bg-green-500 text-white"
                        : index === activeStep
                        ? "border-accent-500 bg-accent-500 text-white"
                        : "border-gray-300 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800"
                    }`}
                  >
                    {index < activeStep ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <div className={`text-sm font-medium ${index > activeStep ? "text-gray-400" : ""}`}>
                      {step.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Current Step Content */}
          <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{steps[activeStep].label}</h2>
                <p className="mt-1 text-gray-600 dark:text-gray-400">{steps[activeStep].description}</p>
              </div>
              <Badge variant="info">{stepLabels[status?.currentStep || "registration"]}</Badge>
            </div>

            {/* Step 1: Registration */}
            {activeStep === 0 && !status?.company && (
              <form onSubmit={handleRegisterCompany} className="mt-6 space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium">Nome da Empresa *</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Nome Fantasia</label>
                  <input
                    type="text"
                    value={tradeName}
                    onChange={(e) => setTradeName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium">CNPJ</label>
                    <input
                      type="text"
                      value={cnpj}
                      onChange={(e) => setCnpj(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">E-mail *</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                      required
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium">Telefone</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">WhatsApp</label>
                    <input
                      type="tel"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium">Segmento</label>
                    <input
                      type="text"
                      value={segment}
                      onChange={(e) => setSegment(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                      placeholder="Ex: Varejo, Indústria"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">Porte</label>
                    <select
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    >
                      <option value="micro">Micro (até 9 funcionários)</option>
                      <option value="small">Pequena (10-49 funcionários)</option>
                      <option value="medium">Média (50-249 funcionários)</option>
                      <option value="large">Grande (250+ funcionários)</option>
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full rounded-lg bg-accent-600 px-4 py-3 font-medium text-white hover:bg-accent-700"
                >
                  Registrar Empresa
                </button>
              </form>
            )}

            {/* Step 2: Documents */}
            {activeStep === 1 && (
              <div className="mt-6 space-y-6">
                <form onSubmit={handleUploadDocument} className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium">Nome do Documento *</label>
                    <input
                      type="text"
                      value={docName}
                      onChange={(e) => setDocName(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">Tipo</label>
                    <select
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    >
                      <option value="document">Documento</option>
                      <option value="contract">Contrato</option>
                      <option value="manual">Manual</option>
                      <option value="checklist">Checklist</option>
                      <option value="other">Outro</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">URL do Documento *</label>
                    <input
                      type="url"
                      value={docUrl}
                      onChange={(e) => setDocUrl(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                      placeholder="https://..."
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Faça upload do documento em um serviço de armazenamento e cole a URL aqui.
                    </p>
                  </div>
                  <button
                    type="submit"
                    className="w-full rounded-lg bg-accent-600 px-4 py-3 font-medium text-white hover:bg-accent-700"
                  >
                    Adicionar Documento
                  </button>
                </form>

                {status?.documents && status.documents.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold">Documentos Enviados</h3>
                    <div className="mt-4 space-y-3">
                      {status.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-gray-800"
                        >
                          <div className="flex items-center gap-3">
                            <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                              <div className="font-medium">{doc.name}</div>
                              <div className="text-sm text-gray-500">{doc.type}</div>
                            </div>
                          </div>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-600 hover:underline"
                          >
                            Ver
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setActiveStep(2)}
                  disabled={!status?.documents || status.documents.length === 0}
                  className="w-full rounded-lg bg-accent-600 px-4 py-3 font-medium text-white hover:bg-accent-700 disabled:opacity-50"
                >
                  Continuar para Agendamento
                </button>
              </div>
            )}

            {/* Step 3: Scheduling */}
            {activeStep === 2 && (
              <form onSubmit={handleSchedule} className="mt-6 space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium">Data Preferida *</label>
                  <input
                    type="date"
                    value={preferredDate}
                    onChange={(e) => setPreferredDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Horário Preferido *</label>
                  <select
                    value={preferredTime}
                    onChange={(e) => setPreferredTime(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    required
                  >
                    <option value="">Selecione...</option>
                    <option value="09:00">09:00</option>
                    <option value="10:00">10:00</option>
                    <option value="11:00">11:00</option>
                    <option value="14:00">14:00</option>
                    <option value="15:00">15:00</option>
                    <option value="16:00">16:00</option>
                    <option value="17:00">17:00</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Observações</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    placeholder="Alguma informação adicional sobre a implantação..."
                  />
                </div>
                <button
                  type="submit"
                  className="w-full rounded-lg bg-accent-600 px-4 py-3 font-medium text-white hover:bg-accent-700"
                >
                  Agendar Implantação
                </button>
              </form>
            )}

            {/* Step 4: Deployment */}
            {activeStep === 3 && (
              <div className="mt-6">
                <div className="rounded-lg bg-accent-50 p-6 dark:bg-accent-900/20">
                  <h3 className="text-lg font-semibold text-accent-900 dark:text-accent-300">
                    Implantação em Andamento
                  </h3>
                  <p className="mt-2 text-accent-700 dark:text-accent-400">
                    Sua implantação está sendo preparada. Nossa equipe entrará em contato para confirmar os detalhes.
                  </p>
                  {status?.schedule && (
                    <div className="mt-4 rounded-lg bg-white p-4 dark:bg-gray-900">
                      <div className="text-sm text-gray-600 dark:text-gray-400">Agendado para:</div>
                      <div className="mt-1 font-semibold">
                        {new Date(status.schedule.startAt).toLocaleDateString("pt-BR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setActiveStep(4)}
                  className="mt-4 w-full rounded-lg bg-accent-600 px-4 py-3 font-medium text-white hover:bg-accent-700"
                >
                  Continuar para Avaliação
                </button>
              </div>
            )}

            {/* Step 5: Evaluation */}
            {activeStep === 4 && (
              <form onSubmit={handleEvaluate} className="mt-6 space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium">Avaliação (1-5 estrelas)</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setScore(star)}
                        className="text-3xl"
                      >
                        {star <= score ? "⭐" : "☆"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Comentários</label>
                  <textarea
                    value={evalNotes}
                    onChange={(e) => setEvalNotes(e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    placeholder="Como foi sua experiência com a implantação?"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full rounded-lg bg-accent-600 px-4 py-3 font-medium text-white hover:bg-accent-700"
                >
                  Enviar Avaliação
                </button>
              </form>
            )}

            {/* Step 6: Completed */}
            {activeStep === 5 && (
              <div className="mt-6 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="mt-4 text-2xl font-bold">Onboarding Concluído!</h3>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  Parabéns! Sua implantação foi concluída com sucesso.
                </p>
                <a
                  href="/dashboard"
                  className="mt-6 inline-block rounded-lg bg-accent-600 px-6 py-3 font-medium text-white hover:bg-accent-700"
                >
                  Ir para Dashboard
                </a>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
